import { createStore, createEvent } from "effector"
import StorageService from "services/StorageService"
import { StorageKey, FilterStorage, FiltersVersion, OneTimeApplicableFilters, FilterCategory } from "config/constants"
import { IAppliedFilter } from "@/types"
import { omit } from "lodash"
import User from "models/User"

export type FilterStorageName = keyof typeof FilterStorage | string

interface IFiltersStore {
    stores: Record<FilterStorageName, IAppliedFilter[]>
    version: number,
    user?: string,
    tenant?: string,
}

const addFilter = createEvent<{ store: FilterStorageName, filter: IAppliedFilter | IAppliedFilter[] }>()
const replaceFilter = createEvent<{ store: FilterStorageName, filterCategory: FilterCategory, filters: IAppliedFilter[] }>()
const removeFilter = createEvent<{ store: FilterStorageName, filter: IAppliedFilter }>()
const setUser = createEvent<{ user?: string, tenant?: string }>()
const reset = createEvent()
const resetStorage = createEvent<FilterStorageName>()

const filtersStoreDefault: IFiltersStore = {
    stores: {
        [FilterStorage.inventory]: [],
        [FilterStorage.request]: [],
        [FilterStorage.offer]: [],
        [FilterStorage.shippingOrder]: [],
        [FilterStorage.outgoingInvoice]: [],
        [FilterStorage.invoice]: [],
        [FilterStorage.valuation]: [],
        [FilterStorage.notifications]: [],
        [FilterStorage.maintenance]: [],
        [FilterStorage.customer]: [],
        [FilterStorage.fairValueEvaluation]: []
    },
    version: FiltersVersion
}

const filtersStore = createStore<IFiltersStore>(getInitialFiltersStore())

function addFilterHandler(existingFilters: IAppliedFilter[], newFilter: IAppliedFilter): IAppliedFilter[] {
    if (typeof newFilter.value === "undefined") {
        return removeFilterHandler(existingFilters, newFilter)
    }
    const newFilterPrepared = newFilter.label ? newFilter : omit(newFilter, "label")
    if (!existingFilters) {
        return [newFilterPrepared]
    }
    const existingFilter = existingFilters.find(f => {
        return f.category === newFilter.category
            && f.value === newFilter.value
    })
    if (existingFilter) {
        return existingFilters
    }
    return [
        ...(OneTimeApplicableFilters.includes(newFilterPrepared.category)
            ? existingFilters.filter(f => f.category !== newFilterPrepared.category)
            : existingFilters
        ),
        newFilterPrepared
    ]
}

filtersStore.on(addFilter, (state: IFiltersStore, { store, filter }) => {
    const filters = Array.isArray(filter) ? filter : [filter]
    return {
        ...state,
        stores: {
            ...state.stores,
            [store]: filters.reduce((acc, f) => addFilterHandler(acc, f), state.stores[store])
        }
    }
})

function replaceFilterHandler(existingFilters: IAppliedFilter[], filterCategory: FilterCategory, newFilters: IAppliedFilter[]): IAppliedFilter[] {
    const filtersToAdd = newFilters.filter(f => typeof f.value !== "undefined" && !existingFilters.some(ef => ef.category === f.category && ef.value === f.value))
    return [
        ...existingFilters.filter(f => f.category !== filterCategory || newFilters.some(nf => nf.category === f.category && nf.value === f.value)),
        ...filtersToAdd,
    ]
}

filtersStore.on(replaceFilter, (state: IFiltersStore, { store, filterCategory, filters }) => {
    return {
        ...state,
        stores: {
            ...state.stores,
            [store]: replaceFilterHandler(state.stores[store], filterCategory, filters)
        }
    }
})

function removeFilterHandler(existingFilters: IAppliedFilter[], filterToRemove: IAppliedFilter): IAppliedFilter[] {
    if (!existingFilters) return []
    return existingFilters.filter(f => {
        if (f.category === filterToRemove.category) {
            if (typeof filterToRemove.value === "undefined") {
                return false
            }
            return f.value !== filterToRemove.value
        }
        return true
    })
}

filtersStore.on(removeFilter, (state: IFiltersStore, { store, filter }) => {
    return {
        ...state,
        stores: {
            ...state.stores,
            [store]: removeFilterHandler(state.stores[store], filter)
        }
    }
})

filtersStore.on(setUser, (state: IFiltersStore, user) => {
    return {
        ...state,
        ...user
    }
})

filtersStore.on(reset, () => filtersStoreDefault)

filtersStore.watch(state => {
    StorageService.setItem(StorageKey.filters, state)
})

filtersStore.on(resetStorage, (state: IFiltersStore, store) => {
    return {
        ...state,
        stores: {
            ...state.stores,
            [store]: filtersStoreDefault.stores[store]
        }
    }
})

function getInitialFiltersStore() {
    try {
        const filtersJson = StorageService.getItem(StorageKey.filters)
        if (!filtersJson) {
            return filtersStoreDefault
        }
        const store: IFiltersStore = JSON.parse(filtersJson)
        store.stores = {
            ...filtersStoreDefault.stores,
            ...store.stores
        }
        if (store) {
            return store
        }
    } catch (e) {
        console.log(e)
    }
    return filtersStoreDefault

}

async function initFiltersStoreUser(user: User | null) {
    const state = filtersStore.getState()
    if (!user?.email
        || user?.email !== state.user
        || !user?.companyId
        || user?.companyId !== state.tenant
        || state.version !== FiltersVersion) {
        reset()
    }
    setUser({
        user: user?.email,
        tenant: user?.companyId,
    })
}

export {
    addFilter,
    addFilterHandler,
    replaceFilter,
    replaceFilterHandler,
    removeFilter,
    removeFilterHandler,
    filtersStore,
    initFiltersStoreUser,
    resetStorage
}
